import type { UseQueryResult } from '@/api/hooks';
import { VITE_APP_BASENAME } from '@/config';
import { useChangeBrowserTabTitleEffect } from '@/hooks';
import { useMessage } from '@/providers';
import { isPS } from '@/utils';
import { isAxiosError } from 'axios';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { matchPath, useLocation } from 'react-router';
import {
  BREAK_POINTS,
  type MinistryKey,
  PAGE_INFO,
  SITE_NAME,
  SITE_URL,
  VIEW_PORT_WIDTH_FOR_SP,
  setCanonicalUrl,
} from '../utils';

export const useChangeBrowserTabTitleEffectForPS = (props: {
  pageKey: keyof typeof PAGE_INFO;
}) => {
  const { pageKey } = props;
  useChangeBrowserTabTitleEffect({
    title: PAGE_INFO[pageKey].title,
    fallback: SITE_NAME,
  });
};

const useCurrentPageInfo = () => {
  const location = useLocation();
  const currentPageInfo = useMemo(
    () =>
      Object.entries(PAGE_INFO)
        .filter(([, value]) => {
          return matchPath(value.path, location.pathname);
        })
        .map(([, value]) => value)[0],
    [location.pathname],
  );

  return { currentPageInfo, location };
};

export const useHandle403ErrorWhenSearching = (queryResult: UseQueryResult) => {
  const { openMessage } = useMessage();
  const { t } = useTranslation();

  useEffect(() => {
    if (isPS() && isAxiosError(queryResult.error)) {
      if (queryResult.error.response?.status === 403) {
        openMessage('error', t('error-message-invalid-search-query'));
      }
    }
  }, [openMessage, queryResult.error, t]);
};

const _useManageCanonicalUrl = () => {
  const { currentPageInfo, location } = useCurrentPageInfo();

  useEffect(() => {
    const path = currentPageInfo?.createPath(location.pathname) ?? null;
    if (path) {
      setCanonicalUrl(`${SITE_URL}${path}`);
    }
  }, [currentPageInfo, location.pathname]);
};

const _useManageViewport = () => {
  const { currentPageInfo } = useCurrentPageInfo();

  useEffect(() => {
    const setViewport = () => {
      const meta = document.querySelector('meta[name=viewport]');

      if (currentPageInfo?.shouldForcePcViewport) {
        if (window.innerWidth < BREAK_POINTS.pcMin) {
          meta?.setAttribute('content', `width=${VIEW_PORT_WIDTH_FOR_SP}`);
        }
      } else {
        meta?.setAttribute('content', 'width=device-width, initial-scale=1.0');
      }
    };

    setViewport();

    window.history.replaceState(null, '', null);
    window.addEventListener('popstate', setViewport);
    return () => {
      window.removeEventListener('popstate', setViewport);
    };
  }, [currentPageInfo]);
};

const _useMinistryInfo = () => {
  const { sp } = useMQ();
  const { t } = useTranslation();

  const ministryInfo: {
    [k in MinistryKey]: {
      imgInfo: {
        src: string;
        alt: string;
        width: number;
      }[];
      href: string;
    };
  } = {
    'cabinet-secretariat-cabinet-office': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/cabinet-secretariat.webp`,
          alt: t('ps-ministry-name-cabinet-secretariat'),
          width: 112,
        },
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/cabinet-office.webp`,
          alt: t('ps-ministry-name-cabinet-office'),
          width: 107,
        },
      ],
      href: 'https://www.cao.go.jp/yosan/review.html',
    },
    'fair-trade-commission': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/fair-trade-commission.webp`,
          alt: t('ps-ministry-name-fair-trade-commission'),
          width: 220,
        },
      ],
      href: 'https://www.jftc.go.jp/soshiki/kyotsukoukai/review/',
    },
    'national-police-agency': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/national-police-agency.webp`,
          alt: t('ps-ministry-name-national-police-agency'),
          width: 152,
        },
      ],
      href: 'https://www.npa.go.jp/policies/budget/review/index.html',
    },
    'personal-information-protection-commission': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/personal-information-protection-commission.webp`,
          alt: t('ps-ministry-name-personal-information-protection-commission'),
          width: 238,
        },
      ],
      href: 'https://www.ppc.go.jp/aboutus/budget/',
    },
    'casino-regulatory-commission': {
      imgInfo: [
        {
          src: sp
            ? `${VITE_APP_BASENAME}/ps/img/ministryLogos/casino-regulatory-commission_sp.webp`
            : `${VITE_APP_BASENAME}/ps/img/ministryLogos/casino-regulatory-commission.webp`,
          alt: t('ps-ministry-name-casino-regulatory-commission'),
          width: sp ? 168 : 275,
        },
      ],
      href: 'https://www.jcrc.go.jp/about/budget/review.html',
    },
    'financial-services-agency': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/financial-services-agency.webp`,
          alt: t('ps-ministry-name-financial-services-agency'),
          width: 128,
        },
      ],
      href: 'https://www.fsa.go.jp/common/budget/kourituka/03.html',
    },
    'consumer-affairs-agency': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/consumer-affairs-agency.webp`,
          alt: t('ps-ministry-name-consumer-affairs-agency'),
          width: 173,
        },
      ],
      href: 'https://www.caa.go.jp/policies/budget/review/',
    },
    'children-and-families-agency': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/children-and-families-agency.webp`,
          alt: t('ps-ministry-name-children-and-families-agency'),
          width: 163,
        },
      ],
      href: 'https://www.cfa.go.jp/project-review/',
    },
    'digital-agency': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/digital-agency.webp`,
          alt: t('ps-ministry-name-digital-agency'),
          width: 147,
        },
      ],
      href: 'https://www.digital.go.jp/policies/assessment/',
    },
    'reconstruction-agency': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/reconstruction-agency.webp`,
          alt: t('ps-ministry-name-reconstruction-agency'),
          width: 119,
        },
      ],
      href: 'https://www.reconstruction.go.jp/topics/main-cat8/sub-cat8-3/20160201143553.html',
    },
    'ministry-of-internal-affairs-and-communications': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-internal-affairs-and-communications.webp`,
          alt: t(
            'ps-ministry-name-ministry-of-internal-affairs-and-communications',
          ),
          width: 142,
        },
      ],
      href: 'https://www.soumu.go.jp/menu_yosan/jigyou.html',
    },
    'ministry-of-justice': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-justice.webp`,
          alt: t('ps-ministry-name-ministry-of-justice'),
          width: 158,
        },
      ],
      href: 'https://www.moj.go.jp/kaikei/bunsho/kaikei03_00026.html',
    },
    'ministry-of-foreign-affairs': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-foreign-affairs.webp`,
          alt: t('ps-ministry-name-ministry-of-foreign-affairs'),
          width: 238,
        },
      ],
      href: 'https://www.mofa.go.jp/mofaj/annai/yosan_kessan/kanshi_kouritsuka/gyosei_review/index.html',
    },
    'ministry-of-finance': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-finance.webp`,
          alt: t('ps-ministry-name-ministry-of-finance'),
          width: 138,
        },
      ],
      href: 'https://www.mof.go.jp/about_mof/mof_budget/review/index.html',
    },
    'ministry-of-education-culture-sports-science-and-technology': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-education-culture-sports-science-and-technology.webp`,
          alt: t(
            'ps-ministry-name-ministry-of-education-culture-sports-science-and-technology',
          ),
          width: 179,
        },
      ],
      href: 'https://www.mext.go.jp/a_menu/kouritsu/detail/1334066.htm',
    },
    'ministry-of-health-labour-and-welfare': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-health-labour-and-welfare.webp`,
          alt: t('ps-ministry-name-ministry-of-health-labour-and-welfare'),
          width: 163,
        },
      ],
      href: 'https://www.mhlw.go.jp/jigyo_shiwake/',
    },
    'ministry-of-agriculture-forestry-and-fisheries': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-agriculture-forestry-and-fisheries.webp`,
          alt: t(
            'ps-ministry-name-ministry-of-agriculture-forestry-and-fisheries',
          ),
          width: 194,
        },
      ],
      href: 'https://www.maff.go.jp/j/budget/review.html',
    },
    'ministry-of-economy-trade-and-industry': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-economy-trade-and-industry.webp`,
          alt: t('ps-ministry-name-ministry-of-economy-trade-and-industry'),
          width: 205,
        },
      ],
      href: 'https://www.meti.go.jp/information_2/publicoffer/review.html',
    },
    'ministry-of-land-infrastructure-and-transport': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-land-infrastructure-and-transport.webp`,
          alt: t(
            'ps-ministry-name-ministry-of-land-infrastructure-and-transport',
          ),
          width: 170,
        },
      ],
      href: 'https://www.mlit.go.jp/policy/file000004.html',
    },
    'ministry-of-the-environment': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-the-environment.webp`,
          alt: t('ps-ministry-name-ministry-of-the-environment'),
          width: 161,
        },
      ],
      href: 'https://www.env.go.jp/guide/budget/spv_eff/review.html',
    },
    'nuclear-regulation-authority': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/nuclear-regulation-authority.webp`,
          alt: t('ps-ministry-name-nuclear-regulation-authority'),
          width: 217,
        },
      ],
      href: 'https://www.nra.go.jp/nra/seisakujikkou/budget/index.html',
    },
    'ministry-of-defense': {
      imgInfo: [
        {
          src: `${VITE_APP_BASENAME}/ps/img/ministryLogos/ministry-of-defense.webp`,
          alt: t('ps-ministry-name-ministry-of-defense'),
          width: 172,
        },
      ],
      href: 'https://www.mod.go.jp/j/approach/hyouka/kouritsuka/rev_suishin/index.html',
    },
  };

  return { ministryInfo };
};

const useMQ = () => {
  const sp = useMediaQuery({
    query: `(max-width: ${BREAK_POINTS.spMax}px)`,
  });

  const tb = useMediaQuery({
    query: `(min-width: ${BREAK_POINTS.spMax + 1}px)`,
  });

  const pcMin = useMediaQuery({
    query: `(min-width: ${BREAK_POINTS.pcMin}px)`,
  });

  const pcMd = useMediaQuery({
    query: `(min-width: ${BREAK_POINTS.pcMd}px)`,
  });

  return { sp, tb, pcMin, pcMd };
};
